import React, { useMemo } from 'react';
import {
  FormControl,
  TextField,
  Autocomplete,
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { vendedorService } from '../../services/vendedor';

interface SelectVendedorProps {
  value: number;
  onChange: (value: number) => void;
  label?: string;
  required?: boolean;
  error?: boolean;
  helperText?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  idPraca?: number;
}

const SelectVendedor: React.FC<SelectVendedorProps> = ({
  value,
  onChange,
  label = 'Vendedor',
  required = false,
  error = false,
  helperText,
  disabled = false,
  fullWidth = true,
  idPraca,
}) => {
  const pracaSelecionada = !!idPraca && idPraca > 0;

  const { data: vendedores = [], isLoading } = useQuery({
    queryKey: ['vendedores', idPraca],
    queryFn: () => vendedorService.getVendedoresByPraca(idPraca as number),
    enabled: pracaSelecionada,
  });

  const sortedVendedores = useMemo(
    () =>
      [...vendedores].sort((a, b) =>
        a.nome.localeCompare(b.nome, 'pt-BR', { sensitivity: 'base' })
      ),
    [vendedores]
  );

  const selected = useMemo(
    () => sortedVendedores.find((v) => v.id === value) || null,
    [sortedVendedores, value]
  );

  const isDisabled = disabled || !pracaSelecionada;

  return (
    <FormControl
      fullWidth={fullWidth}
      required={required}
      error={error}
      disabled={isDisabled}
    >
      <Autocomplete
        options={sortedVendedores}
        getOptionLabel={(v) => v.nome}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        value={selected}
        onChange={(_, newValue) => onChange(newValue?.id || 0)}
        disabled={isDisabled}
        loading={isLoading && pracaSelecionada}
        loadingText="Carregando..."
        noOptionsText={
          pracaSelecionada
            ? 'Nenhum vendedor encontrado'
            : 'Selecione uma praça primeiro'
        }
        renderInput={(params) => (
          <TextField
            {...params}
            label={label}
            required={required}
            error={error}
            helperText={helperText}
          />
        )}
      />
    </FormControl>
  );
};

export default SelectVendedor;
